import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaFacebook, FaInstagram } from 'react-icons/fa';
import { HiSparkles } from 'react-icons/hi2';

/* ─── 2026 Modern Footer ──────────────────────────────────── */
const Footer = () => {
  const location = useLocation();
  const pathname = String(location?.pathname || '');

  const hideOnMobile = (
    pathname.startsWith('/product/') ||
    pathname === '/cart' ||
    pathname === '/checkout' ||
    pathname === '/payment' ||
    pathname === '/summary' ||
    pathname === '/login' ||
    pathname === '/register' ||
    pathname === '/forgot-password' ||
    pathname === '/reset-password' ||
    pathname.startsWith('/ProductListing')
  );

  return (
    <footer
      className={hideOnMobile ? 'hidden md:block' : ''}
      style={{
        background: 'linear-gradient(180deg, #022c22 0%, #064e3b 100%)',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        /* Extra bottom padding on mobile so content clears the bottom nav (64px) */
        paddingBottom: 'calc(env(safe-area-inset-bottom) + 72px)',
      }}
    >
      {/* ── Main grid ── */}
      <div className="container mx-auto px-5 pt-10 pb-0">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-8">

          {/* Brand — full width on mobile */}
          <div className="col-span-2 sm:col-span-1">
            <div className="flex items-center gap-2 mb-3">
              <span
                className="text-white font-black text-2xl"
                style={{ fontFamily: "'Outfit', sans-serif", fontStyle: 'italic' }}
              >
                Trozzi
              </span>
              <HiSparkles className="text-yellow-400 text-sm" />
            </div>
            <p className="text-[13px] text-slate-400 leading-relaxed mb-4">
              Premium shopping experience with the best products at unbeatable prices.
            </p>
            <div className="flex items-center gap-3">
              {[
                { href: 'https://www.facebook.com/share/1CZ3DLG9ry/', icon: FaFacebook, label: 'Facebook' },
                { href: 'https://www.instagram.com/_trozzi_?igsh=YWUyc3BlNnpmbTds', icon: FaInstagram, label: 'Instagram' },
              ].map(({ href, icon: Icon, label }) => (
                <a
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-slate-400 hover:text-white transition-all hover:scale-110"
                  style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  <Icon className="text-base" />
                </a>
              ))}
            </div>
          </div>

          {/* About */}
          <div>
            <h4 className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-4">About</h4>
            <ul className="space-y-3">
              {[
                { to: '/about', label: 'About Us' },
                { to: '/contact', label: 'Contact Us' },
                { to: '/help-center', label: 'Help Center' },
              ].map(({ to, label }) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="text-[13px] text-slate-400 hover:text-white transition-colors"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Help */}
          <div>
            <h4 className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-4">Help</h4>
            <ul className="space-y-3">
              {[
                { to: '/order-tracking', label: 'Track Order' },
                { to: '/shipping-policy', label: 'Shipping Policy' },
                { to: '/refund-policy', label: 'Returns & Refunds' },
              ].map(({ to, label }) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="text-[13px] text-slate-400 hover:text-white transition-colors"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-4">Legal</h4>
            <ul className="space-y-3">
              {[
                { to: '/privacy-policy', label: 'Privacy Policy' },
                { to: '/terms-of-service', label: 'Terms of Use' },
                { to: '/policies', label: 'All Policies' },
              ].map(({ to, label }) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="text-[13px] text-slate-400 hover:text-white transition-colors"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

        </div>
      </div>

      {/* ── Bottom bar — copyright + payments in ONE row ── */}
      <div
        className="container mx-auto px-5 mt-8 pt-4"
        style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
      >
        {/* Single row on desktop, stacked on mobile — both fully visible */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pb-2">

          {/* Copyright */}
          <p className="text-[12px] text-slate-500 text-center sm:text-left">
            © {new Date().getFullYear()} Trozzi. All rights reserved.
          </p>

          {/* Secure Payments */}
          <div className="flex items-center gap-2 flex-wrap justify-center">
            <span className="text-[11px] text-slate-500">Secure Payments:</span>
            {['VISA', 'MC', 'UPI', 'COD'].map((method) => (
              <span
                key={method}
                className="text-[10px] font-bold px-2.5 py-1 rounded-lg"
                style={{
                  background: 'rgba(255,255,255,0.09)',
                  color: '#94a3b8',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                {method}
              </span>
            ))}
          </div>

        </div>
      </div>
    </footer>
  );
};

export default Footer;
